import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useConnectionSpeed } from '../../hooks/useConnectionSpeed';
import { getImagePath } from '../../utils/imagePath';
import styled, { keyframes, css } from 'styled-components';
import imagePreloader from '../../utils/imagePreloader';
import { logger } from '../../utils/logger';
import { useSound } from '../../contexts/SiteContext';

// Animation keyframes - Clean spinning newspaper effect
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

// Spinning newspaper effect from Tutorialzine article
const spinIn = keyframes`
  0% {
    opacity: 0.2;
    transform: scale(0.2) rotate(0deg);
  }
  15% {
    opacity: 1;
    transform: scale(1) rotate(1080deg);
  }
  90% {
    opacity: 1;
    transform: scale(1) rotate(1080deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(1080deg);
  }
`;

const spinOut = keyframes`
  0% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
  85% {
    opacity: 0.2;
    transform: scale(0.2) rotate(-360deg);
  }
  100% {
    opacity: 0.2;
    transform: scale(0.2) rotate(-360deg);
  }
`;

const ModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isClosing',
})`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  cursor: url('/icons/pointer.svg') 10 0, pointer;
  animation: ${props => props.isClosing ? fadeOut : fadeIn} ${props => props.isClosing ? '0.8s' : '0.3s'} ease-out;
  animation-fill-mode: both;
`;

const ModalContent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isClosing' && prop !== 'imageLoaded',
})`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  justify-content: center;
  align-items: center;
  /* Only start animation after image is loaded */
  ${props => {
    if (props.isClosing) {
      return css`animation: ${spinOut} 0.8s linear;`;
    }
    if (!props.imageLoaded) {
      return css`animation: none;`;
    }
    return css`animation: ${spinIn} 1.5s linear;`;
  }}
  animation-fill-mode: both;
  /* Set initial state when image not loaded yet - matches animation start state */
  opacity: ${props => {
    if (props.isClosing) return '1'; // Let closing animation handle it
    if (!props.imageLoaded) return '0.2'; // Match animation start
    return '1'; // Will be animated by spinIn
  }};
  transform: ${props => {
    if (props.isClosing) return 'scale(1) rotate(0deg)'; // Let closing animation handle it
    if (!props.imageLoaded) return 'scale(0.2) rotate(0deg)'; // Match animation start
    return 'scale(0.2) rotate(0deg)'; // Will be animated by spinIn
  }};
`;

const NewspaperImage = styled.img`
  max-width: 100%;
  max-width: min(100%, 1200px); /* Never exceed original image width */
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  transition: transform 0.2s ease;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  font-weight: bold;
  cursor: url('/icons/pointer.svg') 10 0, pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10000;
  opacity: 0;
  animation: ${fadeIn} 0.3s ease-out 1.0s both;

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const NewspaperModal = ({ isOpen, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);
  const { isSoundOn } = useSound();
  const { quality } = useConnectionSpeed();

  const handleClose = useCallback(() => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setIsClosing(false);
      setImageReady(false);
      setImageLoaded(false);
      onClose();
    }, 800); // Match spin-out animation duration
  }, [onClose]);

  // Preload image BEFORE showing modal - this ensures animation starts after image is loaded
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal is closed
      setImageReady(false);
      setImageError(false);
      setImageLoaded(false);
      return;
    }

    const preloadImage = async () => {
      try {
        // Use dynamic quality selection
        const imageSrc = getImagePath('newspaper.jpg', quality);

        // Check if already cached
        if (imagePreloader.isCached(imageSrc)) {
          logger.debug('✅ Newspaper image already cached, ready to show modal');
          setImageError(false);
          setImageReady(true);
          return;
        }

        // Preload the image before showing modal
        logger.debug('📥 Preloading newspaper image before opening modal...');
        await imagePreloader.preload(imageSrc);
        logger.debug('✅ Newspaper image preloaded, ready to show modal');
        setImageError(false);
        setImageReady(true);
      } catch (error) {
        console.error('❌ Failed to preload newspaper image:', error);
        setImageError(true);
        setImageReady(true); // Still show modal even on error, so user can see error message
      }
    };

    preloadImage();
  }, [isOpen, quality]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen && imageReady && !isClosing) {
        handleClose();
      }
    };

    if (isOpen && imageReady) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (!isOpen || !imageReady) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen, imageReady, isClosing, handleClose]);

  // Play newspaper sound when modal opens (only if sound is on and image is loaded)
  useEffect(() => {
    if (isOpen && imageReady && imageLoaded && !isClosing && isSoundOn) {
      const playNewspaperSound = () => {
        try {
          // Use the local MP3 file for newspaper scrunch sound
          const audio = new Audio('/audio/newspaper-scrunch.mp3');
          audio.volume = 0.6;
          audio.preload = 'auto';

          // Play the audio
          audio.play().then(() => {
            logger.debug('Newspaper scrunch sound played from local MP3 file');
          }).catch((error) => {
            console.warn('Could not play local MP3:', error);
          });

        } catch (error) {
          console.warn('Could not create audio object:', error);
        }
      };

      // Play sound after a short delay to sync with spinning animation
      const soundTimeout = setTimeout(playNewspaperSound, 200);

      return () => {
        clearTimeout(soundTimeout);
      };
    }
  }, [isOpen, imageReady, imageLoaded, isClosing, isSoundOn]);

  // Callback ref to check if image is already loaded (e.g., from cache)
  const setImageRef = useCallback((imgElement) => {
    imageRef.current = imgElement;
    if (imgElement && imageReady) {
      // Check if image is already complete (loaded from cache)
      if (imgElement.complete && imgElement.naturalWidth > 0) {
        logger.debug('✅ Newspaper image already loaded (from cache), starting animation');
        setImageError(false);
        // Use requestAnimationFrame to ensure animation starts after browser paints
        requestAnimationFrame(() => {
          setImageLoaded(true);
        });
      }
    }
  }, [imageReady]);

  // Don't render modal until image is ready (preloaded)
  if (!isOpen || !imageReady) return null;

  return (
    <ModalOverlay isClosing={isClosing} onClick={handleClose}>
      <ModalContent isClosing={isClosing} imageLoaded={imageLoaded} onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose} aria-label="Close newspaper">
          ×
        </CloseButton>
        {!imageError ? (
          <NewspaperImage
            ref={setImageRef}
            src={getImagePath('newspaper.jpg', quality)}
            alt="Newspaper"
            onClick={(e) => e.stopPropagation()}
            onError={() => {
              console.error('❌ Newspaper image failed to load in modal');
              setImageError(true);
              // Still set imageLoaded to true so modal shows error message
              setImageLoaded(true);
            }}
            onLoad={() => {
              logger.debug('✅ Newspaper image loaded successfully in modal');
              setImageError(false);
              // Start animation after image is fully loaded
              // Use requestAnimationFrame to ensure animation starts after browser paints
              requestAnimationFrame(() => {
                setImageLoaded(true);
              });
            }}
          />
        ) : (
          <div style={{
            color: 'white',
            fontSize: '1.2rem',
            textAlign: 'center',
            padding: '2rem',
            opacity: 0.8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div>📰</div>
            <div>Newspaper temporarily unavailable</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.6 }}>
              Please try again later
            </div>
          </div>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default NewspaperModal;
